import { getUser, getToken } from "../utils/authService";
import { useState, useEffect } from "react";
import { Camera, Upload, MapPin, Calendar, Search, Filter, Trash2, Download, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SitePhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Upload Dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    project_id: "",
    category: "Progress",
    description: "",
    location_name: "",
    latitude: "",
    longitude: "",
    file: null
  });

  // View Dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const CATEGORIES = ["Progress", "Before Work", "After Work", "Issue/Problem", "Safety", "Quality Check", "Milestone", "Other"];

  const currentUser = getUser() || {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProjects();
    fetchPhotos();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPhotos();
  }, [selectedProject, filterCategory]);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0].id);
        setUploadForm(prev => ({ ...prev, project_id: response.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch projects");
    }
  };

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      let url = '/site-photos';
      const params = [];
      if (selectedProject) params.push(`project_id=${selectedProject}`);
      if (filterCategory !== 'all') params.push(`category=${filterCategory}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await apiClient.get(url);
      setPhotos(response.data);
    } catch (error) {
      toast.error("Failed to fetch photos");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUploadForm(prev => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          }));
          toast.success("Location captured");
        },
        (error) => {
          toast.error("Failed to get location: " + error.message);
        }
      );
    } else {
      toast.error("Geolocation not supported");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error("Please select a photo");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('project_id', uploadForm.project_id);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);
      formData.append('location_name', uploadForm.location_name);
      if (uploadForm.latitude) formData.append('latitude', uploadForm.latitude);
      if (uploadForm.longitude) formData.append('longitude', uploadForm.longitude);

      await apiClient.post('/site-photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Photo uploaded successfully");
      setUploadDialogOpen(false);
      setUploadForm({
        project_id: selectedProject,
        category: "Progress",
        description: "",
        location_name: "",
        latitude: "",
        longitude: "",
        file: null
      });
      fetchPhotos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await apiClient.delete(`/site-photos/${photoId}`);
      toast.success("Photo deleted");
      fetchPhotos();
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const downloadPhoto = async (photo) => {
    try {
      const token = getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-photos/${photo.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = photo.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Failed to download");
    }
  };

  const filteredPhotos = photos.filter(p => 
    searchTerm === "" || 
    p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.location_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">SITE PHOTOS</h1>
          <p className="text-slate-600 mt-2 font-body">Capture and manage site progress photos with GPS location</p>
        </div>
        <button
          onClick={() => setUploadDialogOpen(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Camera className="w-4 h-4" />
          <span>Upload Photo</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>

        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search photos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Photo Grid */}
      {loading ? (
        <div className="text-center py-12">Loading photos...</div>
      ) : filteredPhotos.length > 0 ? (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredPhotos.map(photo => (
            <div key={photo.id} className="stat-card p-0 overflow-hidden group">
              <div className="relative h-48 bg-slate-100">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/site-photos/${photo.id}/thumbnail`}
                  alt={photo.description}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                  <button onClick={() => { setSelectedPhoto(photo); setViewDialogOpen(true); }} className="p-2 bg-white rounded-full hover:bg-slate-100">
                    <Eye className="w-5 h-5 text-primary" />
                  </button>
                  <button onClick={() => downloadPhoto(photo)} className="p-2 bg-white rounded-full hover:bg-slate-100">
                    <Download className="w-5 h-5 text-accent" />
                  </button>
                  <button onClick={() => deletePhoto(photo.id)} className="p-2 bg-white rounded-full hover:bg-slate-100">
                    <Trash2 className="w-5 h-5 text-error" />
                  </button>
                </div>
                <span className="absolute top-2 left-2 px-2 py-1 bg-primary text-white text-xs rounded-sm">{photo.category}</span>
              </div>
              <div className="p-3">
                <p className="font-medium text-sm truncate">{photo.description || 'No description'}</p>
                <div className="flex items-center text-xs text-slate-500 mt-1">
                  <Calendar className="w-3 h-3 mr-1" />
                  {new Date(photo.uploaded_at).toLocaleDateString()}
                </div>
                {photo.location_name && (
                  <div className="flex items-center text-xs text-slate-500 mt-1">
                    <MapPin className="w-3 h-3 mr-1" />
                    {photo.location_name}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 stat-card">
          <Camera className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h3 className="font-heading font-bold text-xl mb-2">No Photos Yet</h3>
          <p className="text-slate-600 mb-4">Upload site progress photos to track project visually.</p>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">UPLOAD SITE PHOTO</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Project *</label>
              <select
                required
                value={uploadForm.project_id}
                onChange={(e) => setUploadForm({...uploadForm, project_id: e.target.value})}
                className="w-full px-4 py-2 border border-slate-300 rounded-sm"
              >
                <option value="">Select Project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Category *</label>
                <select
                  required
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                >
                  {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Location Name</label>
                <input
                  type="text"
                  value={uploadForm.location_name}
                  onChange={(e) => setUploadForm({...uploadForm, location_name: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                  placeholder="e.g., KM 5+200"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Description</label>
              <textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                className="w-full px-4 py-2 border border-slate-300 rounded-sm"
                rows={2}
                placeholder="Describe the photo..."
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-slate-700 uppercase tracking-widest">GPS Location</label>
                <button type="button" onClick={getCurrentLocation} className="text-primary text-xs flex items-center hover:underline">
                  <MapPin className="w-3 h-3 mr-1" /> Get Current Location
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  value={uploadForm.latitude}
                  onChange={(e) => setUploadForm({...uploadForm, latitude: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono text-sm"
                  placeholder="Latitude"
                />
                <input
                  type="text"
                  value={uploadForm.longitude}
                  onChange={(e) => setUploadForm({...uploadForm, longitude: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm font-mono text-sm"
                  placeholder="Longitude"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Photo *</label>
              <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0]})}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Camera className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                  {uploadForm.file ? (
                    <p className="text-sm font-medium text-primary">{uploadForm.file.name}</p>
                  ) : (
                    <p className="text-sm text-slate-600">Click to select or capture photo</p>
                  )}
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={() => setUploadDialogOpen(false)} className="btn-outline">Cancel</button>
              <button type="submit" disabled={uploading} className="btn-primary">
                {uploading ? "Uploading..." : "Upload Photo"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{selectedPhoto?.category} - {selectedPhoto?.project_name}</DialogTitle>
          </DialogHeader>
          {selectedPhoto && (
            <div className="mt-4">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/site-photos/${selectedPhoto.id}/download`}
                alt={selectedPhoto.description}
                className="w-full max-h-[60vh] object-contain bg-slate-100 rounded-sm"
              />
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div><strong>Description:</strong> {selectedPhoto.description || 'N/A'}</div>
                <div><strong>Date:</strong> {new Date(selectedPhoto.uploaded_at).toLocaleString()}</div>
                <div><strong>Location:</strong> {selectedPhoto.location_name || 'N/A'}</div>
                <div><strong>Uploaded By:</strong> {selectedPhoto.uploaded_by}</div>
                {selectedPhoto.latitude && (
                  <div className="col-span-2">
                    <strong>GPS:</strong> {selectedPhoto.latitude}, {selectedPhoto.longitude}
                    <a
                      href={`https://maps.google.com/?q=${selectedPhoto.latitude},${selectedPhoto.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-primary hover:underline"
                    >
                      View on Map
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SitePhotos;
